import { useToast } from '@abyss/web/hooks/useToast';
import { storage } from '@abyss/web/tools/storage';
import { Button } from '@abyss/web/ui/Button';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import DOMPurify from 'dompurify';

import { FilterCollectionModel } from '../../../../common/Constants';
import { applyBccAutoSelectLogic } from '../../../../common/ContentFilters/BccFilterUtils';
import { ProviderSearchFilters } from '../../../../models/ProviderSearch';

interface Props {
  setSearchFilters: (selectedFilters: ProviderSearchFilters) => void;
  selectedFilters: ProviderSearchFilters;
  message: string;
  title: string;
  isPriorityScheduleSelected: boolean;
  toastId: string;
  ids: {
    prioritySchedulingId: string;
    allProvidersId: string;
  };
  icon: string;
  buttonLabel: string;
  providerFilters?: any;
}

const PrioritySchedulingToast = ({
  setSearchFilters,
  selectedFilters,
  message,
  title,
  isPriorityScheduleSelected,
  toastId,
  ids,
  icon,
  buttonLabel,
  providerFilters,
}: Props) => {
  if (storage.session.get(toastId)) {
    return null;
  }

  const { toast } = useToast();
  const { prioritySchedulingId, allProvidersId } = ids;

  const allowedTags = ['b', 'strong'];
  const sanitizedMessage = DOMPurify.sanitize(message, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: [],
  });

  // Hide other toasts when showing a new one
  if (toastId === prioritySchedulingId) {
    toast.hide(allProvidersId);
  } else if (toastId === allProvidersId) {
    toast.hide(prioritySchedulingId);
  }

  return toast.show({
    id: toastId,
    withCloseButton: true,
    autoClose: 20000,
    title,
    message: (
      <>
        <span
          dangerouslySetInnerHTML={{
            __html: sanitizedMessage,
          }}
          data-testid="priority-scheduling-provider-toast-message"
          style={{ color: 'white', display: 'block' }}
        />
        {isPriorityScheduleSelected ? (
          <Button
            css={{
              'abyss-button-root': {
                color: '$white',
                borderBottom: '1px solid $white',
                display: 'block',
                height: 'fit-content',
                marginTop: '4px',
                padding: 0,
                '&:hover': {
                  color: '$white',
                },
              },
            }}
            data-testid="priority-scheduling-provider-toast-undo-button"
            onClick={() => {
              let newSelectedFilter = { ...selectedFilters };
              delete newSelectedFilter[
                FilterCollectionModel.PRIORITY_SCHEDULING
              ];
              newSelectedFilter = applyBccAutoSelectLogic(
                newSelectedFilter,
                providerFilters,
                false
              );
              setSearchFilters(newSelectedFilter);
              storage.session.set(toastId, true);
              storage.session.remove(ids.prioritySchedulingId);
              storage.session.remove(ids.allProvidersId);
              toast.hide(toastId);
            }}
            variant="tertiary"
          >
            {buttonLabel}
          </Button>
        ) : null}
      </>
    ),
    variant: 'info',
    icon: <IconSymbol icon={icon} />,
    onOpen: () => {
      if (
        toastId === prioritySchedulingId &&
        !storage.session.get(allProvidersId)
      ) {
        storage.session.set(allProvidersId, true);
      }
    },
    onClose: () => storage.session.set(toastId, true),
    css: {
      'abyss-toast-container': {
        fontFamily: '$primary',
        maxHeight: 'unset',
      },
    },
  });
};

export default PrioritySchedulingToast;
